import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { QrCode, Clock, Users, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

/** Offentlig side: en frivillig scanner en team-QR og logger ind ved at indtaste navnet.
 *  Ingen email, ingen password — bare scan + tap.
 */
export default function QuickCheckinScan() {
  const { token: qrToken } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [candidates, setCandidates] = useState(null);  // ved disambiguering

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/quick-checkin/info/${qrToken}`)
      .then(res => { if (alive) setInfo(res.data); })
      .catch(e => { if (alive) setError(e.response?.data?.detail || "Kunne ikke læse QR'en"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [qrToken]);

  const submitScan = async (disambiguator_user_id = null) => {
    if (!name.trim() && !disambiguator_user_id) {
      toast.error("Indtast venligst dit navn");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/quick-checkin/scan/${qrToken}`, {
        full_name: name,
        disambiguator_user_id,
      });
      if (res.data.status === "disambiguate") {
        setCandidates(res.data.candidates);
        toast.info(`${res.data.candidates.length} personer matcher — tap dig selv`);
      } else {
        // Login succes — gem token og redirect
        localStorage.setItem("token", res.data.token);
        localStorage.setItem("quickCheckinSession", JSON.stringify({
          session_id: res.data.session_id,
          role_name: res.data.role_name,
          expires_at: res.data.expires_at,
        }));
        // Bind festival direkte, så QR-frivillige springer event-vælgeren over
        // og aldrig ser andre festivaler.
        if (res.data.event_id) {
          localStorage.setItem("selectedEventId", res.data.event_id);
          localStorage.setItem("selectedEventName", res.data.event_name || info.role_name);
          localStorage.setItem("selectedEventArchived", "false");
          localStorage.removeItem("selectedEventLogoUrl");
        }
        toast.success(`Velkommen! Logget ind som ${info.role_name}`);
        // Lille forsinkelse så toast'en kan ses
        setTimeout(() => { window.location.href = "/"; }, 600);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fejl ved indtjekning");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center text-white">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center text-white p-6">
        <div className="max-w-sm w-full bg-zinc-800 rounded-xl p-6 text-center border border-red-500/30">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
          <h1 className="text-lg font-bold mb-2">QR-kode ugyldig</h1>
          <p className="text-sm text-zinc-300 mb-4">{error}</p>
          <Button onClick={() => navigate("/login")} variant="outline" className="text-zinc-900">
            Brug email-login i stedet
          </Button>
        </div>
      </div>
    );
  }

  if (!info?.is_active) {
    const from = new Date(info.valid_from);
    const until = new Date(info.valid_until);
    const now = new Date();
    const notYet = now < from;
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center text-white p-6">
        <div className="max-w-sm w-full bg-zinc-800 rounded-xl p-6 text-center border border-yellow-500/30">
          <Clock className="h-12 w-12 text-yellow-400 mx-auto mb-3" />
          <h1 className="text-lg font-bold mb-2">{info.role_name}</h1>
          <p className="text-sm text-zinc-300 mb-4">
            {notYet
              ? `Denne QR aktiveres ${from.toLocaleString('da-DK', { weekday: 'long', day: 'numeric', month: 'long' })} kl. ${from.toLocaleString('da-DK', { hour: '2-digit', minute: '2-digit' })}.`
              : `Denne QR udløb kl. ${until.toLocaleString('da-DK', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}.`}
          </p>
          <p className="text-xs text-zinc-500">Find en ny QR for i dag — eller spørg din leder.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-900 to-zinc-950 flex items-center justify-center text-white p-6">
      <div className="max-w-md w-full">
        <div className="bg-zinc-800 rounded-xl shadow-2xl border border-zinc-700 overflow-hidden">
          {/* Header */}
          <div className="bg-emerald-600 px-6 py-5">
            <div className="flex items-center gap-3 mb-2">
              <QrCode className="h-6 w-6" />
              <span className="text-xs uppercase tracking-wider font-mono opacity-80">Hurtig indtjekning</span>
            </div>
            <h1 className="text-2xl font-bold leading-tight" data-testid="qc-role-name">{info.role_name}</h1>
            <div className="flex items-center gap-3 mt-2 text-xs text-emerald-100">
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {info.session_duration_hours} t. session</span>
              {info.capacity ? (
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {info.active_count}/{info.capacity}
                  {info.spots_left === 0 ? <span className="font-bold ml-1">FYLDT</span> : null}
                </span>
              ) : null}
            </div>
          </div>

          {/* Body */}
          <div className="p-6">
            {info.spots_left === 0 ? (
              <div className="text-center py-6">
                <AlertCircle className="h-10 w-10 text-yellow-400 mx-auto mb-2" />
                <p className="text-sm text-zinc-300 mb-2">Holdet er fyldt lige nu.</p>
                <p className="text-xs text-zinc-500">Vent til en kollega tjekker ud — eller spørg din leder.</p>
              </div>
            ) : candidates ? (
              <div>
                <p className="text-sm text-zinc-300 mb-3">Vi fandt flere med dette navn. Tap dig selv:</p>
                <div className="space-y-2">
                  {candidates.map(c => (
                    <button
                      key={c.user_id}
                      onClick={() => submitScan(c.user_id)}
                      disabled={submitting}
                      className="w-full text-left bg-zinc-700 hover:bg-zinc-600 active:bg-zinc-500 rounded-lg px-4 py-3 transition-colors disabled:opacity-50"
                      data-testid={`qc-candidate-${c.user_id}`}
                    >
                      <div className="font-medium">{c.name}</div>
                      <div className="text-[11px] text-zinc-400 font-mono">{c.email_hint}</div>
                    </button>
                  ))}
                </div>
                <Button onClick={() => { setCandidates(null); setName(""); }} variant="ghost" className="w-full mt-3 text-zinc-300">
                  Tilbage
                </Button>
              </div>
            ) : (
              <>
                <label className="text-xs uppercase tracking-wider text-zinc-400 block mb-2">
                  Indtast dit fulde navn
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && submitScan()}
                  placeholder="fx Søren Jensen"
                  autoFocus
                  autoComplete="name"
                  className="w-full bg-zinc-900 border border-zinc-700 focus:border-emerald-500 rounded-lg px-4 py-3 text-base text-white outline-none transition-colors"
                  data-testid="qc-name-input"
                />
                <p className="text-[11px] text-zinc-500 mt-1.5">
                  Brug det navn du brugte til at tilmelde dig festivalen.
                </p>
                <Button
                  onClick={() => submitScan()}
                  disabled={submitting || !name.trim()}
                  className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="qc-submit-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Tjek ind
                </Button>
              </>
            )}
          </div>
        </div>

        <p className="text-center text-[11px] text-zinc-500 mt-4">
          Ingen password krævet · Session udløber automatisk
        </p>
      </div>
    </div>
  );
}
